import { useEffect, useMemo, useState } from "react";
import { X, ShoppingBag, Plus, Minus, Trash2, CheckCircle2, Phone, Clock, NotebookPen } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

const parsePrice = (it) => {
  if (it.price) {
    const m = String(it.price).match(/(\d+\.?\d*)/);
    if (m) return Number(m[1]);
  }
  if (it.prices && it.prices.length) {
    for (const p of it.prices) {
      if (!p) continue;
      const m = String(p).match(/(\d+\.?\d*)/);
      if (m) return Number(m[1]);
    }
  }
  return 0;
};

export default function PickupOrderModal({ restaurant, menu, onClose }) {
  const [activeMenuIdx, setActiveMenuIdx] = useState(0);
  const [cart, setCart] = useState({}); // {itemKey: {name, price, qty}}
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [pickupTime, setPickupTime] = useState("ASAP");
  const [notes, setNotes] = useState("");
  const [step, setStep] = useState("menu"); // menu | checkout | success
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const menus = menu?.menus || [];
  const activeMenu = menus[activeMenuIdx];

  const addItem = (it) => {
    const price = parsePrice(it);
    if (price === 0) {
      setError(`${it.name} has no fixed price for online order — please ring the restaurant.`);
      return;
    }
    setError(null);
    const key = String(it.name || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-");
    setCart((c) => {
      const cur = c[key] || { name: it.name, price, qty: 0 };
      return { ...c, [key]: { ...cur, qty: Math.min(cur.qty + 1, 20) } };
    });
  };
  const decItem = (key) =>
    setCart((c) => {
      const cur = c[key];
      if (!cur) return c;
      if (cur.qty <= 1) {
        const { [key]: _drop, ...rest } = c;
        return rest;
      }
      return { ...c, [key]: { ...cur, qty: cur.qty - 1 } };
    });
  const removeItem = (key) =>
    setCart((c) => {
      const { [key]: _drop, ...rest } = c;
      return rest;
    });

  const lineItems = useMemo(() => Object.entries(cart).map(([key, v]) => ({ key, ...v })), [cart]);
  const total = useMemo(
    () => lineItems.reduce((sum, l) => sum + l.price * l.qty, 0),
    [lineItems],
  );
  const totalQty = useMemo(() => lineItems.reduce((sum, l) => sum + l.qty, 0), [lineItems]);

  useEffect(() => {
    if (lineItems.length === 0 && step === "checkout") setStep("menu");
  }, [lineItems.length, step]);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const r = await fetch(
        `${API}/api/directory/restaurants/${restaurant.slug}/pickup-order`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            phone,
            pickup_time: pickupTime,
            notes,
            items: lineItems.map((l) => ({ name: l.name, price: l.price, qty: l.qty })),
          }),
        },
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Could not place order");
      setSuccess(data.order);
      setStep("success");
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4">
      <div
        data-testid="pickup-order-modal"
        className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <div>
            <h2 className="font-serif text-xl text-stone-900">Pre-order pickup</h2>
            <p className="text-[12px] text-stone-500">from {restaurant.name}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === "menu" && (
          <>
            {menus.length > 1 && (
              <div className="px-4 py-2 border-b border-stone-100 overflow-x-auto flex gap-2">
                {menus.map((m, i) => (
                  <button
                    key={m.id}
                    onClick={() => setActiveMenuIdx(i)}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-[12px] font-medium border ${
                      activeMenuIdx === i ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200"
                    }`}
                  >{m.menu_name}</button>
                ))}
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              {activeMenu?.sections.map((s) => (
                <div key={s.name}>
                  <h3 className="font-serif text-[15px] text-stone-900 mb-2 border-b border-stone-200 pb-1">{s.name}</h3>
                  {s.items.map((it, idx) => {
                    const price = parsePrice(it);
                    return (
                      <div key={idx} className="flex items-start gap-2 py-1.5">
                        <div className="flex-1 min-w-0">
                          <div className="text-[14px] font-medium text-stone-900 capitalize">{it.name}</div>
                          {it.description && (
                            <div className="text-[12px] text-stone-500 line-clamp-2">{it.description}</div>
                          )}
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="text-[13px] font-semibold tabular-nums text-stone-700">
                            {price > 0 ? fmt(price) : (it.prices?.filter(Boolean).join(" / ") || "—")}
                          </div>
                          <button
                            onClick={() => addItem(it)}
                            data-testid={`add-item-${idx}`}
                            disabled={price === 0}
                            className="mt-1 px-3 py-1 rounded-full text-[12px] font-semibold bg-stone-900 text-white disabled:bg-stone-300 disabled:cursor-not-allowed"
                          >
                            + Add
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {error && (
              <div className="px-4 py-2 bg-rose-50 border-t border-rose-200 text-rose-700 text-[13px]">{error}</div>
            )}

            <div className="border-t border-stone-200 px-4 py-3 flex items-center gap-3 bg-white">
              <div className="flex-1">
                <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Cart</div>
                <div className="text-[15px] font-semibold text-stone-900">
                  {totalQty} item{totalQty !== 1 ? "s" : ""} · {fmt(total)}
                </div>
              </div>
              <button
                onClick={() => setStep("checkout")}
                disabled={totalQty === 0}
                data-testid="goto-checkout"
                className="px-5 py-3 rounded-xl bg-stone-900 text-white font-semibold disabled:opacity-40"
              >
                Checkout
              </button>
            </div>
          </>
        )}

        {step === "checkout" && (
          <div className="overflow-y-auto p-5 space-y-4">
            <div>
              <h3 className="font-serif text-[15px] text-stone-900 mb-2">Your order</h3>
              {lineItems.map((l) => (
                <div key={l.key} className="flex items-center gap-2 py-1.5 border-b border-stone-100 last:border-0">
                  <button onClick={() => removeItem(l.key)} className="text-stone-300 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-medium text-stone-900 capitalize truncate">{l.name}</div>
                    <div className="text-[11px] text-stone-500">{fmt(l.price)} each</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => decItem(l.key)} className="w-7 h-7 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                    <span className="w-6 text-center text-[14px] font-semibold tabular-nums">{l.qty}</span>
                    <button onClick={() => setCart((c) => ({ ...c, [l.key]: { ...c[l.key], qty: Math.min(c[l.key].qty + 1, 20) } }))} className="w-7 h-7 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                  </div>
                  <div className="w-16 text-right text-[14px] font-semibold tabular-nums">{fmt(l.price * l.qty)}</div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 mt-2 border-t border-stone-200">
                <span className="font-semibold">Total</span>
                <span className="font-semibold text-lg tabular-nums">{fmt(total)}</span>
              </div>
            </div>

            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Your name</span>
              <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="First & last name"
                className="w-full mt-1 px-3 py-2.5 rounded-lg border border-stone-200 text-[14px]" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1.5"><Phone className="w-3 h-3" /> Phone</span>
              <input required type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0400 000 000"
                className="w-full mt-1 px-3 py-2.5 rounded-lg border border-stone-200 text-[14px]" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1.5"><Clock className="w-3 h-3" /> Pickup time</span>
              <select value={pickupTime} onChange={(e) => setPickupTime(e.target.value)}
                className="w-full mt-1 px-3 py-2.5 rounded-lg border border-stone-200 text-[14px] bg-white">
                <option>ASAP</option>
                <option>In 15 min</option>
                <option>In 30 min</option>
                <option>In 45 min</option>
                <option>In 1 hour</option>
                <option>In 2 hours</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1.5"><NotebookPen className="w-3 h-3" /> Notes</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows="2" placeholder="Allergies, milk preferences…"
                className="w-full mt-1 px-3 py-2 rounded-lg border border-stone-200 text-[14px]" />
            </label>

            {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-[13px] rounded-lg px-3 py-2">{error}</div>}

            <div className="flex gap-2">
              <button onClick={() => setStep("menu")} className="flex-1 px-4 py-3 rounded-xl border border-stone-200 text-stone-700 font-semibold">Back</button>
              <button onClick={submit} disabled={submitting || !name || !phone}
                data-testid="place-order"
                className="flex-[2] px-4 py-3 rounded-xl bg-stone-900 text-white font-semibold disabled:opacity-50">
                {submitting ? "Placing…" : `Place order · ${fmt(total)}`}
              </button>
            </div>
            <p className="text-[11px] text-stone-400 text-center">Demo only — no payment is taken.</p>
          </div>
        )}

        {step === "success" && success && (
          <div className="p-6 text-center">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
            <h3 className="font-serif text-2xl text-stone-900 mb-2">Order placed!</h3>
            <p className="text-stone-600 text-[14px] mb-4">
              {restaurant.name} will have it ready at {success.pickup_time === "ASAP" ? "the earliest" : success.pickup_time.toLowerCase()}.
            </p>
            <div className="bg-stone-50 rounded-lg p-4 text-left text-[13px] space-y-1 mb-4">
              <div><span className="text-stone-400">Order ID: </span><span className="font-mono text-[11px]">{success.id.slice(0, 8)}</span></div>
              <div><span className="text-stone-400">Items: </span>{success.items.length}</div>
              <div><span className="text-stone-400">Total: </span><span className="font-semibold">{fmt(success.total)}</span></div>
              <div><span className="text-stone-400">Pickup at: </span>{success.pickup_time}</div>
            </div>
            <button onClick={onClose} className="w-full px-4 py-3 rounded-xl bg-stone-900 text-white font-semibold">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
